/**
 * How much room is left in a capped text field.
 *
 * WHY THIS IS NOT ALWAYS VISIBLE. A counter sitting under every box from
 * the first keystroke is clutter that tells the user nothing -- at 12 of
 * 20000 characters the number is noise, and noise trains people not to
 * read it. It appears only once the limit is close enough to matter, so
 * that when it does appear it is worth looking at.
 *
 * The failure it exists to prevent is specific: the server rejects an
 * over-length body with a 400, which arrives AFTER the user has finished
 * writing. For a journal that is the worst possible placement of a
 * limit, because long entries are the point of the feature. Same shape
 * as #455's two invisible limits -- a rule the app enforces and never
 * mentions is found by hitting it.
 *
 * `maxLength` on the input is what actually stops the typing; this only
 * explains why. Both are needed: the attribute without the counter is a
 * keyboard that silently stops working.
 */

/* Show the last 10%, floored at 200 characters so a short field does not
   reveal its counter after three words. */
export function visibleFrom(max) {
  return Math.max(max - Math.max(Math.round(max * 0.1), 200), 0);
}

export function CharacterCount({ value, max, id }) {
  const used = (value || "").length;
  if (used < visibleFrom(max)) return null;

  const left = max - used;
  const full = left <= 0;

  return (
    <p
      id={id}
      /* polite, not assertive: this updates on every keystroke near the
         limit, and assertive would interrupt the user mid-sentence on
         every one of them. */
      aria-live="polite"
      style={{
        margin: "var(--space-1) 0 0",
        fontSize: "var(--font-size-sm)",
        color: full ? "var(--color-danger)" : "var(--color-text-muted)",
      }}
    >
      {full
        ? "You have reached the limit for one entry. Start another to keep going."
        : `${left.toLocaleString()} characters left`}
    </p>
  );
}
